/**
 * Copyright © 2026 Adnuntius AS.
 */
import * as _ from 'lodash';
import {firstValueFrom} from 'rxjs';

import {PERMISSIONS} from '../../../components/auth/auth-constants';
import {TeamApiService} from '../team/team-api.service';
import {UserApiService} from '../user/user-api.service';

export interface RolePermissionConfig {
  applications: string[];
  isSuiPlatform: boolean;
  permissionByType: Record<string, any[]>;
}

export function buildRolePermissionConfig(LocalNetworkProfile: any): RolePermissionConfig {
  const applications: string[] = [];
  const apps: string[] = [];

  if (LocalNetworkProfile.isDataPlatform()) {
    applications.push('DATA');
    apps.push('DATA');
  }
  if (LocalNetworkProfile.isAdvertisingPlatform()) {
    applications.push('ADVERTISING');
    apps.push('ADVERTISING');
  }
  if (LocalNetworkProfile.isSuiPlatform()) {
    apps.push('SELF_SERVICE');
  }
  if (LocalNetworkProfile.isMarketplacePlatform()) {
    apps.push('MARKETPLACE');
  }
  if (LocalNetworkProfile.get('politicalApp')) {
    applications.push('POLITICAL');
    apps.push('POLITICAL');
  }

  const permissionByType = {
    INTERNAL: _.map(_.filter(PERMISSIONS, (permission) => {
      return (!permission.roleType || permission.roleType === 'INTERNAL')
        && apps.indexOf(permission.app) > -1
        && permission.application && applications.indexOf(permission.application) > -1
        && (!permission.onlyInMarketplace || LocalNetworkProfile.isMarketplacePlatform())
        && (!permission.checkAdvancedLogging || LocalNetworkProfile.get('advancedLogging'));
    }), (permission) => permission),
    EXTERNAL: _.map(_.filter(PERMISSIONS, (permission) => {
      return permission.roleType === 'EXTERNAL' && apps.indexOf(permission.app) > -1;
    }), (permission) => permission)
  };

  return {
    applications,
    isSuiPlatform: LocalNetworkProfile.isSuiPlatform(),
    permissionByType
  };
}

export function setupRoleModel(model: any, applications: string[], localUserProfile: any): void {
  model.application = model.application || localUserProfile.getCurrentApp() || '';
  if (applications.length === 1) {
    model.application = applications[0];
  } else if (applications.indexOf(model.application) < 0) {
    model.application = applications[0] || model.application;
  }

  model.roleType = _.find(model.permissions, (permission) => {
    const perm = _.find(PERMISSIONS, (foundPerm) => {
      return foundPerm.apiKey === permission || foundPerm.apiKey === permission.apiKey;
    });
    return perm && perm.roleType === 'EXTERNAL';
  }) ? 'EXTERNAL' : 'INTERNAL';

  if (model.application === 'POLITICAL') {
    model.roleType = 'INTERNAL';
  }

  model.scope = model.scope || 'NETWORK';
  model.permissions = model.permissions || [];
}

export function cleanupPermissionsOnSubmit(model: any): void {
  _.remove(model.permissions, (permission) => {
    const foundPerm = _.find(PERMISSIONS, ['apiKey', permission]);
    return foundPerm ? foundPerm.scope !== model.scope
      || (!!foundPerm.roleType && foundPerm.roleType !== model.roleType) : false;
  });
}

export function loadRoleAnalysisUsers(
  model: any,
  teamApi: TeamApiService,
  userApi: UserApiService,
  LocalNetworkProfile: any
): Promise<any[]> {
  return new Promise((resolve, reject) => {
    firstValueFrom(teamApi.listOptions({includeHidden: true, includeInactive: true})).then((teamPage) => {
      const teams = teamPage.results;
      firstValueFrom(userApi.listOptions()).then((userPage) => {
        const users: any[] = _.filter(userPage.results, (user: any) => {
          const rolesByNetwork = _.find(user.userRoles.rolesByNetwork, ['network.tagId', LocalNetworkProfile.getNetworkId()]);
          if (!rolesByNetwork) {
            return false;
          }
          const matchingRoles = _.filter(rolesByNetwork.roles, ['role.id', model.id]);
          if (matchingRoles.length > 0) {
            user.teams = _.map(matchingRoles, (roleAssignment) => {
              if (!roleAssignment.team) {
                return {name: 'Current network'};
              }
              return _.find(teams, ['id', roleAssignment.team.id]);
            });
          }
          return user.teams;
        });
        _.forEach(users, (user: any) => {
          user.obfuscated = user.username.indexOf('@') < 0 && user.username.indexOf('*') > 0;
        });
        resolve(users);
      }, reject);
    }, reject);
  });
}
